import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Tag, PencilSimple, Trash, Package } from "@phosphor-icons/react";
import { api, formatError } from "@/lib/api";
import AuthedImage from "@/components/AuthedImage";
import AddItemModal from "@/components/AddItemModal";
import { formatLocationPath } from "@/lib/locationPath";
import { toast } from "sonner";

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [allLocations, setAllLocations] = useState([]);
  const [category, setCategory] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    try {
      const [{ data }, { data: locs }] = await Promise.all([
        api.get(`/items/${id}`),
        api.get("/locations"),
      ]);
      setItem(data);
      setAllLocations(locs || []);
      if (data.category_id) {
        const cs = await api.get("/categories");
        setCategory(cs.data.find((c) => c.id === data.category_id) || null);
      } else setCategory(null);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const del = async () => {
    if (!confirm("Delete this item?")) return;
    await api.delete(`/items/${id}`);
    toast.success("Item deleted");
    navigate("/items");
  };

  if (!item) return <div className="text-stoneink-500" data-testid="item-loading">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="item-detail">
      <Link to="/items" className="inline-flex items-center gap-2 text-stoneink-700 hover:text-terracotta-600 text-sm" data-testid="back-to-items">
        <ArrowLeft size={16} /> Back to items
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="aspect-square rounded-3xl overflow-hidden border border-sand-300 bg-sand-200 shadow-ambient">
          {item.photo_path ? (
            <AuthedImage path={item.photo_path} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full grid place-items-center text-stoneink-500/60">
              <Package weight="duotone" size={72} />
            </div>
          )}
        </motion.div>

        <div className="space-y-6">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">{item.name}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-stoneink-700">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sand-200 text-sm">
                <Package size={14} /> Qty {item.quantity}
              </span>
              {item.location_id && (() => {
                const path = formatLocationPath(allLocations, item.location_id);
                if (!path) return null;
                return (
                  <span
                    data-testid="item-location-path"
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sage-400/20 text-sage-600 text-sm"
                    title={path}
                  >
                    <MapPin size={14} /> {path}
                  </span>
                );
              })()}
              {category && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm" style={{ backgroundColor: `${category.color}20`, color: category.color }}>
                  <Tag size={14} /> {category.name}
                </span>
              )}
            </div>
          </div>

          {item.description && (
            <div className="rounded-3xl border border-sand-300 bg-white p-6 shadow-ambient">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Notes</div>
              <p className="mt-2 text-stoneink-700 whitespace-pre-wrap leading-relaxed">{item.description}</p>
            </div>
          )}

          {item.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {item.tags.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full bg-sand-200 text-stoneink-700 text-sm">{t}</span>
              ))}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button data-testid="edit-item-btn" onClick={() => setEditing(true)} className="h-12 px-5 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium flex items-center gap-2 active:scale-[0.98] transition-all">
              <PencilSimple weight="bold" size={16} /> Edit
            </button>
            <button data-testid="delete-item-btn" onClick={del} className="h-12 px-5 rounded-xl bg-white border border-sand-300 hover:border-destructive hover:text-destructive text-stoneink-700 flex items-center gap-2 transition-all">
              <Trash size={16} /> Delete
            </button>
          </div>
        </div>
      </div>

      <AddItemModal open={editing} onClose={() => setEditing(false)} onCreated={load} initial={item} />
    </div>
  );
}
